import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows } from '@visx/grid';
import type { ScaleBand, ScaleLinear } from '@visx/vendor/d3-scale';

import type { PlotBox } from './plot.tsx';

/**
 * Axes and gridlines wearing the dashboard's tokens.
 *
 * visx takes its styling as props, so the token classes are applied here once
 * rather than at every call site. Ink is `--engrave` and gridlines are
 * `--hairline`: both recessive, so the marks stay the loudest thing in the plot.
 */

const TICK_LABEL = {
  className: 'chart-axis-label',
  fill: 'var(--engrave)',
  fontSize: 10,
  fontFamily: 'var(--mono)',
} as const;

export function ChartGrid({ box, scale, ticks = 4 }: {
  box: PlotBox;
  scale: ScaleLinear<number, number>;
  ticks?: number;
}) {
  return (
    <GridRows
      scale={scale}
      width={box.innerWidth}
      height={box.innerHeight}
      numTicks={ticks}
      className="chart-gridline"
      stroke="var(--hairline)"
      strokeWidth={1}
    />
  );
}

export function ChartValueAxis({ scale, ticks = 4, format }: {
  scale: ScaleLinear<number, number>;
  ticks?: number;
  format: (value: number) => string;
}) {
  return (
    <AxisLeft
      scale={scale}
      numTicks={ticks}
      hideAxisLine
      hideTicks
      tickFormat={(value) => format(Number(value))}
      tickLabelProps={() => ({ ...TICK_LABEL, textAnchor: 'end', dx: -6, dy: 3 })}
    />
  );
}

/**
 * Day axis.
 *
 * `interval` thins the labels rather than the bars: a 30-day window has no room to
 * name every column, but every column is still drawn and still hoverable.
 */
export function ChartDayAxis({ box, scale, interval, format }: {
  box: PlotBox;
  scale: ScaleBand<string>;
  interval: number;
  format: (day: string) => string;
}) {
  const domain = scale.domain();
  return (
    <AxisBottom
      top={box.innerHeight}
      scale={scale}
      hideAxisLine
      hideTicks
      tickFormat={(day) => {
        const index = domain.indexOf(String(day));
        // Anchor the thinning to the end so the most recent day is always named.
        return (domain.length - 1 - index) % interval === 0 ? format(String(day)) : '';
      }}
      tickLabelProps={() => ({ ...TICK_LABEL, textAnchor: 'middle', dy: 2 })}
    />
  );
}

/**
 * Continuous time axis, for a chart whose x is wall clock rather than a bucket.
 *
 * Spacing is real elapsed time, so a multi-month range reads as months instead of
 * evenly spaced samples. Ends are anchored inward so the first and last labels do
 * not hang off the plot.
 */
export function ChartTimeAxis({ box, scale, format, ticks = 3 }: {
  box: PlotBox;
  scale: ScaleLinear<number, number>;
  format: (at: number) => string;
  ticks?: number;
}) {
  const [from, to] = scale.domain() as [number, number];
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
  const marks = Array.from({ length: ticks }, (_, index) => from + ((to - from) * index) / (ticks - 1));
  const seen = new Set<string>();
  return (
    <>
      {marks.map((at, index) => {
        const label = format(at);
        // A short range can render the same date three times; draw it once.
        if (seen.has(label)) return null;
        seen.add(label);
        return (
          <text
            key={label}
            className="chart-axis-label"
            fill="var(--engrave)"
            fontSize={10}
            fontFamily="var(--mono)"
            x={scale(at)}
            y={box.innerHeight + 18}
            textAnchor={index === 0 ? 'start' : index === marks.length - 1 ? 'end' : 'middle'}
          >
            {label}
          </text>
        );
      })}
    </>
  );
}

/** Label every Nth day so the axis never collides with itself. */
export function dayLabelInterval(days: number, innerWidth: number): number {
  if (days <= 0 || innerWidth <= 0) return 1;
  const perLabel = 52;
  const affordable = Math.max(1, Math.floor(innerWidth / perLabel));
  return Math.max(1, Math.ceil(days / affordable));
}
